/**
 * Who owns a given library — the person whose name goes on it.
 *
 * The backend attaches an `owner` to every library, carrying a user id, an email and a name, so
 * the surfaces that render "Owned by X" can name a person rather than an opaque account. Read
 * against `user._id`, which a signed-in session always has.
 *
 * **This does not answer "whose credits does adding media here spend?"** That is
 * `resolveBillingOwnership`, keyed on `Library.billingId`, and the two genuinely come apart: an
 * org or SSO account puts several users on one billing account, so a teammate's library is
 * owned by somebody else and billed to the reader's own wallet. Deciding the billing question
 * from ownership skips the pre-flight balance gates on a library the reader really does pay
 * for, tells them "their credits, not yours" about their own money, and withholds the top-up
 * CTA after a 402 while insisting a top-up wouldn't help. Only `billingId` may relax a gate;
 * only this module may name a person. A surface needing both — the cross-billing notice — takes
 * the flag from one and the name from the other, and tolerates them disagreeing.
 *
 * Deliberately three-valued. `unknown` is not a synonym for `self`: it means one of the two ids
 * never arrived (a library fetched before `/user/me` resolved, a store whose last fetch failed
 * and nulled `data`), or that the caller ruled the question out — `LibraryStore.ownership`
 * forces `unknown` on the demo and read-only libraries, which are platform fixtures rather than
 * anybody's property. The only safe reading is "don't claim anything": callers must map
 * `unknown` onto today's behaviour, never onto a claim.
 */
export type LibraryOwnership = 'self' | 'other' | 'unknown';

/** The shape of `Library['owner']`, narrowed to what this module reads. */
export type LibraryOwnerLike = {
  userId?: string;
  email?: string;
  name?: {
    first?: string;
    last?: string;
    nickname?: string;
  };
};

/**
 * Compare a library's owner against the current user.
 *
 * Both ids must be present and non-empty to return a verdict; anything else is `unknown`.
 * Note the asymmetry that follows from that: a missing id can never produce `other`, so an
 * absent field costs us a line we should have shown, never a line we shouldn't have. That's
 * the correct trade — the false positive puts a stranger's name on the reader's own library.
 */
export function resolveLibraryOwnership(
  libraryOwnerUserId: string | null | undefined,
  currentUserId: string | null | undefined,
): LibraryOwnership {
  if (!libraryOwnerUserId || !currentUserId) return 'unknown';
  return libraryOwnerUserId === currentUserId ? 'self' : 'other';
}

/**
 * The single predicate every consumer should branch on: true only when we positively know
 * somebody else owns the library. Keeps `unknown` from ever reaching an owner name — see the
 * type docstring. Never gate an upload or a balance check on this; that's
 * `isBilledToAnotherAccount`.
 */
export function isOwnedByAnotherUser(ownership: LibraryOwnership): boolean {
  return ownership === 'other';
}

/**
 * A human label for an owner, or `undefined` when the backend gave us nothing to print.
 *
 * Nickname first, matching how the product names people everywhere else (the avatar initial,
 * the workspace greeting, the Sentry user) — it's the only one of the three the user chose for
 * themselves. Email is the last resort rather than the first because it's the owner's, not the
 * reader's, and exposing it is a bigger step than showing a display name.
 *
 * Returning `undefined` is a real outcome, not an error case: an owner with no name and no
 * email is a library we know is somebody else's but cannot attribute. The upload surfaces pair
 * this with `isBilledToAnotherAccount` for exactly that reason — the fact that somebody else
 * pays and the name of who that is are separately knowable, and the fact still needs saying
 * when the name is missing.
 */
export function formatOwnerName(owner: LibraryOwnerLike | null | undefined): string | undefined {
  if (!owner) return undefined;

  const nickname = owner.name?.nickname?.trim();
  if (nickname) return nickname;

  const full = [owner.name?.first?.trim(), owner.name?.last?.trim()].filter(Boolean).join(' ');
  if (full) return full;

  return owner.email?.trim() || undefined;
}

/**
 * The one wording for "somebody else owns this library", shared by the three surfaces that show
 * it: the library grid card's meta line, the mobile library row's meta line, and the libraries
 * table's Name cell. A bare name in those places is ambiguous — beside "24 media · 3.4 GB" it
 * could be read as a contributor, an uploader or a folder — and three files each inventing their
 * own gloss is how "by X" and "X's library" end up on screen at the same time.
 *
 * Takes a definite name rather than the optional one, because there is nothing to say without
 * it: callers reach this through `LibraryStore.otherOwnerName`, which is already `undefined`
 * both for your own libraries and for an owner the backend couldn't name.
 */
export function ownedByLabel(ownerName: string): string {
  return `Owned by ${ownerName}`;
}
